/**
 * Platform settings (D-32), in tabs. Each tab saves only its own fields, so an unsaved edit in one
 * tab is never sent by accident from another. Values reach all API and worker processes within
 * about 10 seconds. Env-only values are listed read-only in the Server tab.
 */
import {
  LOG_LEVELS,
  type LogLevel,
  MESSAGE_PRIORITIES,
  type MessagePriority,
  type SystemSettings,
  type SystemSettingsView,
} from "@aiyo/shared";
import { Alert, Button, Chip, Tabs, toast } from "@heroui/react";
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { FieldStack, SelectInput, SwitchInput, TextAreaInput, TextInput } from "../components/fields";
import {
  Callout,
  Code,
  ErrorBlock,
  InlineError,
  KeyValue,
  LoadingBlock,
  Page,
  PageHeader,
  Panel,
} from "../components/ui";
import { validationIssues } from "../lib/api";
import { useFormat } from "../lib/format";
import { useMe, useSettings, useUpdateSettings } from "../lib/queries";

interface Draft {
  public_url: string;
  trusted_origins: string;
  session_ttl_hours: string;
  provider_timeout_ms: string;
  high: string;
  normal: string;
  low: string;
  high_on: boolean;
  normal_on: boolean;
  low_on: boolean;
  log_level: LogLevel;
  map_style_light: string;
  map_style_dark: string;
}

function toDraft(s: SystemSettings): Draft {
  return {
    public_url: s.public_url,
    trusted_origins: s.trusted_origins.join("\n"),
    session_ttl_hours: String(s.session_ttl_hours),
    provider_timeout_ms: String(s.provider_timeout_ms),
    high: String(s.worker_concurrency.high),
    normal: String(s.worker_concurrency.normal),
    low: String(s.worker_concurrency.low),
    high_on: !s.paused_lanes.includes("high"),
    normal_on: !s.paused_lanes.includes("normal"),
    low_on: !s.paused_lanes.includes("low"),
    log_level: s.log_level,
    map_style_light: s.map_style_light,
    map_style_dark: s.map_style_dark,
  };
}

type Section = "general" | "access" | "delivery";
const SECTIONS: Section[] = ["general", "access", "delivery"];
/** Draft key for whether a lane runs (the settings store the paused ones). */
const laneOn = (lane: MessagePriority) => `${lane}_on` as const;

const SECTION_FIELDS: Record<Section, Array<keyof Draft>> = {
  general: ["public_url", "log_level", "map_style_light", "map_style_dark"],
  access: ["trusted_origins", "session_ttl_hours"],
  delivery: ["provider_timeout_ms", "high", "normal", "low", "high_on", "normal_on", "low_on"],
};
const TABS = ["general", "access", "delivery", "environment"] as const;
type Tab = (typeof TABS)[number];

function sectionDirty(draft: Draft, base: Draft, section: Section) {
  return SECTION_FIELDS[section].some((k) => draft[k] !== base[k]);
}

function copyFields(target: Draft, source: Draft, section: Section): Draft {
  const next: Record<string, unknown> = { ...target };
  for (const k of SECTION_FIELDS[section]) next[k] = source[k];
  return next as unknown as Draft;
}

/** PATCH body with only the fields of one section. */
function sectionBody(d: Draft, section: Section) {
  if (section === "general") {
    return {
      public_url: d.public_url.trim(),
      log_level: d.log_level,
      map_style_light: d.map_style_light.trim(),
      map_style_dark: d.map_style_dark.trim(),
    };
  }
  if (section === "access") {
    return {
      trusted_origins: d.trusted_origins
        .split(/\r?\n/)
        .map((s) => s.trim().replace(/\/+$/, ""))
        .filter(Boolean),
      session_ttl_hours: Number(d.session_ttl_hours),
    };
  }
  return {
    provider_timeout_ms: Number(d.provider_timeout_ms),
    worker_concurrency: { high: Number(d.high), normal: Number(d.normal), low: Number(d.low) },
    paused_lanes: MESSAGE_PRIORITIES.filter((lane) => !d[laneOn(lane)]),
  };
}

export function SettingsPage() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const isSuperadmin = Boolean(me?.is_superadmin);
  const settings = useSettings(isSuperadmin);

  return (
    <Page>
      <PageHeader title={t("settings.title")} description={t("settings.subtitle")} />
      {!isSuperadmin ? (
        <Alert>
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>{t("settings.superadminOnly")}</Alert.Description>
          </Alert.Content>
        </Alert>
      ) : settings.isPending ? (
        <LoadingBlock />
      ) : settings.isError ? (
        <ErrorBlock error={settings.error} onRetry={() => settings.refetch()} />
      ) : (
        <SettingsTabs view={settings.data} />
      )}
    </Page>
  );
}

function SettingsTabs({ view }: { view: SystemSettingsView }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const update = useUpdateSettings();
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") ?? "";
  const tab: Tab = (TABS as readonly string[]).includes(requested) ? (requested as Tab) : "general";
  const base = useMemo(() => toDraft(view.settings), [view.settings]);
  const [draft, setDraft] = useState<Draft>(base);
  const [saving, setSaving] = useState<Section | null>(null);
  const [failed, setFailed] = useState<Section | null>(null);
  const previousBase = useRef(base);

  // After a save the server copy changes: take the new values for every section without
  // unsaved edits, and keep the edits of the others.
  useEffect(() => {
    const old = previousBase.current;
    previousBase.current = base;
    setDraft((d) =>
      SECTIONS.reduce((acc, s) => (sectionDirty(d, old, s) ? acc : copyFields(acc, base, s)), d),
    );
  }, [base]);

  const dirty = (section: Section) => sectionDirty(draft, base, section);
  const errors: Record<string, string> = Object.fromEntries(
    validationIssues(update.error).map((i) => [i.path.split(".")[0] ?? i.path, i.message]),
  );
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));
  const save = (section: Section) => (e: FormEvent) => {
    e.preventDefault();
    setSaving(section);
    setFailed(null);
    update.mutate(sectionBody(draft, section), {
      onSuccess: () => toast.success(t("settings.saved")),
      onError: () => setFailed(section),
      onSettled: () => setSaving(null),
    });
  };

  const SectionForm = ({
    section,
    title,
    children,
  }: {
    section: Section;
    title: string;
    children: ReactNode;
  }) => (
    <form onSubmit={save(section)} className="max-w-3xl" aria-label={title}>
      <Panel>
        <FieldStack>{children}</FieldStack>
        {failed === section && Object.keys(errors).length === 0 ? <InlineError error={update.error} /> : null}
        <div className="flex flex-col gap-3 border-t border-separator pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted">
            {dirty(section)
              ? t("settings.unsaved")
              : view.updated_at
                ? t("settings.lastUpdated", {
                    when: fmt.relative(view.updated_at),
                    who: view.updated_by ?? "?",
                  })
                : t("settings.neverUpdated")}
          </p>
          <div className="flex gap-2">
            <Button
              variant="tertiary"
              isDisabled={!dirty(section)}
              onPress={() => setDraft((d) => copyFields(d, base, section))}
            >
              {t("settings.discard")}
            </Button>
            <Button type="submit" isDisabled={!dirty(section)} isPending={saving === section}>
              {t("common.saveChanges")}
            </Button>
          </div>
        </div>
      </Panel>
    </form>
  );

  const env = view.environment;
  const yesNo = (v: boolean) => (v ? t("common.yes") : t("common.no"));
  const setOrMissing = (v: boolean) => (v ? t("common.set") : t("common.missing"));

  return (
    <Tabs selectedKey={tab} onSelectionChange={(key) => setParams({ tab: String(key) }, { replace: true })}>
      <Tabs.ListContainer>
        <Tabs.List aria-label={t("settings.title")}>
          {TABS.map((id) => (
            <Tabs.Tab key={id} id={id}>
              <span className="flex items-center gap-2">
                {t(`settings.tabs.${id}`)}
                {id !== "environment" && dirty(id) ? (
                  <span
                    className="size-1.5 rounded-full bg-warning"
                    role="img"
                    aria-label={t("settings.unsavedTab")}
                  />
                ) : null}
              </span>
              <Tabs.Indicator />
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs.ListContainer>

      <Tabs.Panel id="general" className="px-0 pt-8">
        {SectionForm({
          section: "general",
          title: t("settings.general"),
          children: (
            <>
              <TextInput
                name="public_url"
                type="url"
                label={t("settings.publicUrl")}
                description={t("settings.publicUrlHint")}
                placeholder="https://signal.example.com"
                value={draft.public_url}
                onChange={(v) => set("public_url", v)}
                error={errors.public_url}
              />
              <SelectInput
                label={t("settings.logLevel")}
                description={t("settings.logLevelHint")}
                value={draft.log_level}
                onChange={(v) => v && set("log_level", v as LogLevel)}
                options={LOG_LEVELS.map((l) => ({ id: l, label: l }))}
              />
              <TextInput
                name="map_style_light"
                type="url"
                label={t("settings.mapStyleLight")}
                description={t("settings.mapStyleHint")}
                placeholder="https://tiles.openfreemap.org/styles/positron"
                value={draft.map_style_light}
                onChange={(v) => set("map_style_light", v)}
                error={errors.map_style_light}
              />
              <TextInput
                name="map_style_dark"
                type="url"
                label={t("settings.mapStyleDark")}
                description={t("settings.mapStyleHint")}
                placeholder="https://tiles.openfreemap.org/styles/dark"
                value={draft.map_style_dark}
                onChange={(v) => set("map_style_dark", v)}
                error={errors.map_style_dark}
              />
            </>
          ),
        })}
      </Tabs.Panel>

      <Tabs.Panel id="access" className="px-0 pt-8">
        {SectionForm({
          section: "access",
          title: t("settings.access"),
          children: (
            <>
              <TextAreaInput
                name="trusted_origins"
                label={t("settings.trustedOrigins")}
                description={t("settings.trustedOriginsHint")}
                placeholder="https://dashboard.example.com"
                value={draft.trusted_origins}
                onChange={(v) => set("trusted_origins", v)}
                error={errors.trusted_origins}
                rows={4}
                mono
              />
              {env.trusted_origins.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <span className="text-sm text-muted">{t("settings.envOrigins")}</span>
                  <div className="flex flex-wrap gap-2">
                    {env.trusted_origins.map((o) => (
                      <Chip key={o} size="sm" variant="secondary">
                        {o}
                      </Chip>
                    ))}
                  </div>
                </div>
              ) : null}
              <TextInput
                name="session_ttl_hours"
                type="number"
                label={t("settings.sessionTtl")}
                description={t("settings.sessionTtlHint")}
                value={draft.session_ttl_hours}
                onChange={(v) => set("session_ttl_hours", v)}
                error={errors.session_ttl_hours}
                min={1}
                max={720}
              />
            </>
          ),
        })}
      </Tabs.Panel>

      <Tabs.Panel id="delivery" className="px-0 pt-8">
        {SectionForm({
          section: "delivery",
          title: t("settings.delivery"),
          children: (
            <>
              <TextInput
                name="provider_timeout_ms"
                type="number"
                label={t("settings.providerTimeout")}
                description={t("settings.providerTimeoutHint")}
                value={draft.provider_timeout_ms}
                onChange={(v) => set("provider_timeout_ms", v)}
                error={errors.provider_timeout_ms}
                min={1000}
                max={120000}
              />
              <fieldset className="flex flex-col gap-3">
                <legend className="mb-1 text-sm font-medium">{t("settings.lanes")}</legend>
                <p className="text-xs leading-relaxed text-muted">{t("settings.lanesHint")}</p>
                <div className="flex flex-col divide-y divide-separator rounded-2xl border border-separator">
                  {MESSAGE_PRIORITIES.map((lane: MessagePriority) => {
                    const on = draft[laneOn(lane)];
                    return (
                      <div
                        key={lane}
                        className="grid grid-cols-1 items-center gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_9rem_8rem]"
                      >
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <span className="text-sm font-medium">{t(`priority.${lane}`)}</span>
                          <span className="text-xs text-muted">{t(`composer.priorityHint.${lane}`)}</span>
                        </div>
                        <SwitchInput
                          label={on ? t("settings.laneRunning") : t("settings.lanePaused")}
                          isSelected={on}
                          onChange={(v) => set(laneOn(lane), v)}
                        />
                        <TextInput
                          name={`concurrency_${lane}`}
                          type="number"
                          label={t("settings.laneConcurrency")}
                          value={draft[lane]}
                          onChange={(v) => set(lane, v)}
                          min={1}
                          max={200}
                        />
                      </div>
                    );
                  })}
                </div>
                {MESSAGE_PRIORITIES.some((lane) => !draft[laneOn(lane)]) ? (
                  <Callout tone="warning">{t("settings.pausedWarning")}</Callout>
                ) : null}
                <p className="text-xs text-muted">{t("settings.concurrencyHint")}</p>
                {errors.worker_concurrency ? (
                  <p className="text-xs text-danger">{errors.worker_concurrency}</p>
                ) : null}
              </fieldset>
            </>
          ),
        })}
      </Tabs.Panel>

      <Tabs.Panel id="environment" className="px-0 pt-8">
        <Panel description={t("settings.environmentHint")} className="max-w-3xl">
          <KeyValue
            items={[
              { label: t("settings.env.nodeEnv"), value: <Code>{env.node_env}</Code> },
              { label: t("settings.env.port"), value: env.port },
              { label: t("settings.env.poolMax"), value: env.database_pool_max },
              { label: t("settings.env.databaseUrl"), value: setOrMissing(env.database_url_set) },
              { label: t("settings.env.masterKey"), value: setOrMissing(env.credential_master_key_set) },
              { label: t("settings.env.keyVersion"), value: env.credential_key_version },
              { label: t("settings.env.cookieSecure"), value: yesNo(env.cookie_secure) },
              { label: t("settings.env.cookieSameSite"), value: <Code>{env.cookie_samesite}</Code> },
              {
                label: t("settings.env.origins"),
                value: env.trusted_origins.length ? env.trusted_origins.join(", ") : t("common.none"),
              },
            ]}
          />
        </Panel>
      </Tabs.Panel>
    </Tabs>
  );
}
